import {
  meQueryFn,
  meQueryKey,
  parseActiveCompanyIdFromCompaniesWithLogtime,
  useFetchCompaniesWithLogtime,
} from "@lhd/hooks";
import { setCompany } from "@lhd/features/Company/companySlice";
import { userLogin } from "@lhd/features/users/usersSlice";
import { user } from "@lhd/features/users/selector";
import { Company } from "@lhd/types";
import { getCompanyId, setCompanyId } from "@lhd/utility";
import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import axios from "axios";
import { find } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const getSyncCompanyErrorMessage = (error: unknown): string => {
  const defaultMessage = "会社の切り替えに失敗しました。";
  if (!axios.isAxiosError(error)) return defaultMessage;

  const data = error.response?.data as
    | { message?: string; msg?: string | string[] }
    | undefined;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.msg === "string" && data.msg) return data.msg;
  const msgList = data?.msg;
  if (Array.isArray(msgList) && msgList[0]) return String(msgList[0]);

  return defaultMessage;
};

export const resolveDefaultTrackingCompanyId = (
  companies: Company[],
): string => {
  if (!companies.length) return "";
  const defaultCompany =
    companies.find((c) => c.is_work_default) ?? companies[0];
  return defaultCompany.id.toString();
};

export const useTrackingCompanyId = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const auth = useSelector(user);
  const companies = auth?.companies ?? [];
  const [manualCompanyId, setManualCompanyId] = useState<string | null>(null);
  const [isSwitchingCompany, setIsSwitchingCompany] = useState(false);
  const lastSyncedCompanyRef = useRef<string | null>(null);

  const defaultCompanyId = useMemo(
    () => resolveDefaultTrackingCompanyId(companies),
    [companies],
  );

  const companyIds = useMemo(
    () => companies.map((c) => c.id).filter((id) => Number.isFinite(id)),
    [companies],
  );

  const { data: companiesWithLogtimeData } =
    useFetchCompaniesWithLogtime(companyIds);

  const activeCompanyFromApi = useMemo(
    () =>
      parseActiveCompanyIdFromCompaniesWithLogtime(companiesWithLogtimeData),
    [companiesWithLogtimeData],
  );

  const storedCompanyId = getCompanyId();

  const resolvedCompanyId = useMemo(() => {
    if (!companies.length) return "";

    const isValidStored =
      !!storedCompanyId &&
      companies.some((c) => c.id.toString() === storedCompanyId);

    if (activeCompanyFromApi) return activeCompanyFromApi;
    if (isValidStored) return storedCompanyId;
    return defaultCompanyId;
  }, [activeCompanyFromApi, companies, defaultCompanyId, storedCompanyId]);

  const selectedCompanyId = manualCompanyId ?? resolvedCompanyId;

  const syncAuthForCompany = useCallback(
    async (companyId: string): Promise<boolean> => {
      if (!companyId || lastSyncedCompanyRef.current === companyId) {
        return true;
      }

      const loginCompanyId = Number(companyId);
      if (!Number.isFinite(loginCompanyId) || loginCompanyId <= 0) {
        return false;
      }

      setIsSwitchingCompany(true);
      try {
        const meQuery = meQueryKey(companyId, loginCompanyId);
        const meResult = await queryClient.fetchQuery({
          queryKey: meQuery,
          queryFn: meQueryFn,
          staleTime: 5000,
        });

        lastSyncedCompanyRef.current = companyId;
        queryClient.setQueryData(meQuery, meResult);
        setCompanyId(companyId);

        const userData = meResult.data;
        dispatch(userLogin({ user: userData }));

        const companyActive =
          find(
            userData.companies,
            (company) => company.id.toString() === companyId,
          ) || userData.companies?.[0];
        if (companyActive) {
          dispatch(setCompany({ company: companyActive }));
        }
        return true;
      } catch (error) {
        notification.error({ message: getSyncCompanyErrorMessage(error) });
        return false;
      } finally {
        setIsSwitchingCompany(false);
      }
    },
    [dispatch, queryClient],
  );

  useEffect(() => {
    if (!resolvedCompanyId || manualCompanyId) return;
    void syncAuthForCompany(resolvedCompanyId);
  }, [manualCompanyId, resolvedCompanyId, syncAuthForCompany]);

  const onCompanyChange = async (id: string) => {
    if (!id || id === selectedCompanyId) return;

    const ok = await syncAuthForCompany(id);
    if (ok) {
      setManualCompanyId(id);
    }
  };

  return {
    companies,
    selectedCompanyId,
    onCompanyChange,
    isSwitchingCompany,
  };
};
